import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { fetchClientJourneyProgressReport } from '../../../../store/thunks/reportsThunk';

const ClientJourneyProgressReport = () => {
  const dispatch = useDispatch();
  
  // Filter states
  const [selectedJourneyTemplate, setSelectedJourneyTemplate] = useState('all');
  const [selectedClinic, setSelectedClinic] = useState('all');
  const [selectedClinician, setSelectedClinician] = useState('all');

  // View state
  const [viewMode, setViewMode] = useState('summary'); // 'summary', 'detailed', 'charts'

  // Get data from Redux store
  const {
    data: reportData,
    loading,
    error,
  } = useSelector((state) => state.reports.clientJourneyProgress);

  const generateReport = async () => {
    try {
      await dispatch(fetchClientJourneyProgressReport({
        journeyTemplateId: selectedJourneyTemplate,
        clinicId: selectedClinic,
        clinicianId: selectedClinician,
      })).unwrap();
      
      toast.success('Journey progress report generated successfully');
    } catch (err) {
      toast.error('Failed to generate report: ' + (err.message || err));
    }
  };

  useEffect(() => {
    generateReport();
  }, []);

  const handleFilterChange = () => {
    generateReport();
  };

  const exportToCSV = () => {
    if (!reportData?.data?.journeys) return;

    const headers = [
      'Client Name',
      'Client Email', 
      'Journey Name',
      'Status',
      'Progress %',
      'Current Section',
      'Current Step',
      'Steps Completed',
      'Total Steps',
      'Treating Clinician',
      'Clinic',
      'Started At',
      'Last Activity'
    ];

    const csvData = reportData.data.journeys.map(journey => [
      journey.clientName,
      journey.clientEmail,
      journey.journeyName,
      journey.status,
      journey.progressPercentage,
      journey.currentSectionName,
      journey.currentStepName,
      journey.completedSteps,
      journey.totalSteps,
      journey.treatingClinician,
      journey.clinic,
      new Date(journey.startedAt).toLocaleDateString(),
      new Date(journey.lastActivity).toLocaleDateString()
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `client-journey-progress-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 80) return 'text-green-600 bg-green-100';
    if (percentage >= 50) return 'text-yellow-600 bg-yellow-100';
    if (percentage >= 25) return 'text-orange-600 bg-orange-100';
    return 'text-red-600 bg-red-100';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-700 bg-green-100';
      case 'completed': return 'text-blue-700 bg-blue-100';
      case 'paused': return 'text-yellow-700 bg-yellow-100';
      case 'cancelled': return 'text-red-700 bg-red-100';
      default: return 'text-gray-700 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-800">
          <h3 className="font-medium">Error generating report</h3>
          <p className="mt-1 text-sm">{error.message || error}</p>
          <button
            onClick={generateReport}
            className="mt-2 bg-red-600 text-white px-3 py-1 rounded-sm text-sm hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Extract data from Redux response structure
  const data = reportData?.data || {};
  const summary = data.summary || {};
  const journeys = data.journeys || [];
  const availableTemplates = data.availableTemplates || [];
  const availableClinics = data.availableClinics || [];
  const availableClinicians = data.availableClinicians || [];

  return (
    <div className="space-y-6">
      {/* Header and Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Client Journey Progress Report</h2>
          <div className="flex gap-2">
            <button
              onClick={exportToCSV}
              className="bg-green-600 text-white px-4 py-2 rounded-sm text-sm hover:bg-green-700"
              disabled={!journeys?.length}
            >
              Export CSV
            </button>
            <button
              onClick={handleFilterChange}
              className="bg-indigo-600 text-white px-4 py-2 rounded-sm text-sm hover:bg-indigo-700"
            >
              Refresh Report
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Journey Template</label>
            <select
              value={selectedJourneyTemplate}
              onChange={(e) => setSelectedJourneyTemplate(e.target.value)}
              className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
            >
              <option value="all">All Journey Templates</option>
              {availableTemplates.map(template => (
                <option key={template._id} value={template._id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Clinic</label>
            <select
              value={selectedClinic}
              onChange={(e) => setSelectedClinic(e.target.value)}
              className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
            >
              <option value="all">All Clinics</option>
              {availableClinics.map(clinic => (
                <option key={clinic._id} value={clinic._id}>
                  {clinic.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Clinician</label>
            <select
              value={selectedClinician}
              onChange={(e) => setSelectedClinician(e.target.value)}
              className="w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-indigo-500"
            >
              <option value="all">All Clinicians</option>
              {availableClinicians.map(clinician => (
                <option key={clinician._id} value={clinician._id}>
                  {clinician.firstName} {clinician.lastName}
                </option>
              ))}
            </select>
          </div>
        </div>

        <button
          onClick={handleFilterChange}
          className="bg-indigo-600 text-white px-4 py-2 rounded-sm text-sm hover:bg-indigo-700"
        >
          Apply Filters
        </button>
      </div>

      {/* View Mode Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'summary', label: 'Summary' },
              { id: 'detailed', label: 'Detailed View' },
              { id: 'charts', label: 'Charts & Analytics' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setViewMode(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  viewMode === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {viewMode === 'summary' && data && (
            <div className="space-y-6">
              {/* Summary Stats */}
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-gray-900">{summary.totalClients}</div>
                  <div className="text-sm text-gray-600">Total Clients</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-green-600">{summary.activeJourneys}</div>
                  <div className="text-sm text-gray-600">Active Journeys</div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-blue-600">{summary.completedJourneys}</div>
                  <div className="text-sm text-gray-600">Completed</div>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-600">{summary.pausedJourneys}</div>
                  <div className="text-sm text-gray-600">Paused</div>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg">
                  <div className="text-2xl font-bold text-indigo-600">{summary.averageProgress}%</div>
                  <div className="text-sm text-gray-600">Avg Progress</div>
                </div>
              </div>

              {/* Progress Distribution */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Progress Distribution</h3>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                  {Object.entries(summary.progressDistribution || {}).map(([range, count]) => (
                    <div key={range} className="bg-gray-50 p-3 rounded text-center">
                      <div className="text-lg font-semibold text-gray-900">{count}</div>
                      <div className="text-xs text-gray-600">{range}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Clinician Stats */}
              {Object.keys(summary.clinicianStats || {}).length > 0 && (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Clinician Performance</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Clinician
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Clients
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Avg Progress
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Active
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Completed
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(summary.clinicianStats || {}).map(([id, stats]) => (
                          <tr key={id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {stats.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {stats.totalClients}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {stats.averageProgress}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {stats.activeJourneys || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {stats.completedJourneys || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {viewMode === 'detailed' && data && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Detailed Client Journey Progress</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Journey
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Progress
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Current Step
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Clinician
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Activity
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {journeys.map((journey) => (
                      <tr key={journey.journeyId}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{journey.clientName}</div>
                            <div className="text-sm text-gray-500">{journey.clientEmail}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {journey.journeyName}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(journey.status)}`}>
                            {journey.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-full bg-gray-200 rounded-full h-2 mr-2">
                              <div 
                                className="bg-indigo-600 h-2 rounded-full" 
                                style={{ width: `${journey.progressPercentage}%` }}
                              ></div>
                            </div>
                            <span className={`text-xs px-2 py-1 rounded ${getProgressColor(journey.progressPercentage)}`}>
                              {journey.progressPercentage}%
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {journey.completedSteps}/{journey.totalSteps} steps
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{journey.currentSectionName}</div>
                          <div className="text-sm text-gray-500">{journey.currentStepName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {journey.treatingClinician}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(journey.lastActivity).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {viewMode === 'charts' && data && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Charts & Analytics</h3>
              
              {/* Step Completion Stats */}
              {Object.keys(summary.stepCompletionStats || {}).length > 0 && (
                <div>
                  <h4 className="text-md font-medium text-gray-700 mb-3">Most Completed Steps</h4>
                  <div className="space-y-2">
                    {Object.entries(summary.stepCompletionStats || {})
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 10)
                      .map(([stepName, count]) => (
                        <div key={stepName} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                          <span className="text-sm text-gray-900">{stepName}</span>
                          <span className="text-sm font-medium text-indigo-600">{count} completions</span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Clinic Stats */}
              {Object.keys(summary.clinicStats || {}).length > 0 && (
                <div>
                  <h4 className="text-md font-medium text-gray-700 mb-3">Clinic Performance</h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Clinic
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Total Clients
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Average Progress
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Active Journeys
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Completed Journeys
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {Object.entries(summary.clinicStats || {}).map(([id, stats]) => (
                          <tr key={id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {stats.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {stats.totalClients}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {stats.averageProgress}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {stats.activeJourneys || 0}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {stats.completedJourneys || 0}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientJourneyProgressReport;